// Delisted analogs arrive with their symbol suffixed "-DELISTED" (e.g. "CRR-DELISTED")
// ONLY when FirstRateData needed to disambiguate a symbol collision (the ticker was
// later reused by a different, currently-listed company) — most delisted tickers, whose
// symbol was never reused, are stored bare and carry no suffix at all. The API's
// `delisted: boolean` field (Match.delisted / StockData.delisted) is the authoritative
// signal for the whole population; the suffix regex below is kept only as a fallback for
// callers that genuinely have nothing else (e.g. a bare symbol string with no API object
// behind it) — prefer passing the real flag wherever it's available.
//
// IMPORTANT: use these ONLY for DISPLAY. The raw suffixed symbol is still the identity
// key (dataset toggles, colors, lookups), so don't transform those.

const DELISTED_RE = /-delisted$/i;

/** Fallback-only: infers delisted status from the "-DELISTED" suffix convention, which
 * covers only FirstRateData's symbol-collision cases. Prefer the API's `delisted` field. */
export function isDelisted(symbol: string | null | undefined): boolean {
  return !!symbol && DELISTED_RE.test(symbol);
}

/** The symbol without the "-DELISTED" suffix (unchanged if not delisted). */
export function baseSymbol(symbol: string | null | undefined): string {
  return (symbol ?? "").replace(DELISTED_RE, "");
}

/** Display form of a ticker: "CRR*" for delisted, otherwise the (suffix-stripped)
 * symbol. Pass the API's `delisted` field explicitly when available — it covers the
 * whole delisted population, not just the suffix-covered collision subset. Falls back
 * to the suffix heuristic only when `delisted` is omitted. */
export function displaySymbol(symbol: string | null | undefined, delisted?: boolean): string {
  const s = symbol ?? "";
  const isDel = delisted ?? isDelisted(s);
  return isDel ? `${baseSymbol(s)}*` : baseSymbol(s);
}

/** Display form of a company name: strip a trailing "-DELISTED" if the name carries it. */
export function displayName(name: string | null | undefined): string {
  return baseSymbol(name);
}
